import { createClient } from '@supabase/supabase-js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase/client';
import { supabaseFreeTable } from '../utils/supabase/clientFreeTable';
import { useAuth } from './AuthProvider';




export const useReservations = () => {
    const {profile} = useAuth(); 
  
  return useQuery({
    queryKey: ['reservations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reservations')
        .select('*').order("time").eq("restaurantid",profile.restaurantid);
      if (error) throw error;

      // Ensure name and status are never null
      return data;
    },
    enabled: !!supabase.auth.getSession(),
  });
};

export const useReservationById = (reservationId) => {
  return useQuery({
    queryKey: ['reservation', reservationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reservations')
        .select('*')
        .eq('id', reservationId)
        .single();
      if (error) throw error;
      if (!data) throw new Error(`Reservation with ID ${reservationId} not found`);
      return {
        ...data,
        name: data.name || 'Unknown',
        status: data.status || 'pending',
      };
    },
    enabled: !!reservationId && !!supabase.auth.getSession(),
  });
};

export const useUpdateReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reservationId, tableIds }) => {
      const { data, error } = await supabase
        .from('reservations')
        .update({ tableIds })
        .eq('id', reservationId)
        .select('*');
      if (error) throw error;
      return data[0];
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservation', data.id] });
    },
  });
};

export const useAddReservation = () => {
  const queryClient = useQueryClient();
    const {profile} = useAuth(); 


  return useMutation({
    mutationFn: async ({ name, partySize, time, tableIds, status = 'pending', prefered_place, freeTableId }) => {
      const { data, error } = await supabase
        .from('reservations')
        .insert([{ name: name || 'Unknown', partySize, time, tableIds, status: status || 'pending', prefered_place: prefered_place||null, freeTableId:freeTableId||null,restaurantid:profile.restaurantid }])
        .select('id, time, name, partySize, tableIds, status, prefered_place, freeTableId');
      if (error) throw error;
      return data[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
};

export const useUpdateReservationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reservationId, status }) => {
      const { data, error } = await supabase
        .from('reservations')
        .update({ status })
        .eq('id', reservationId)
        .select('id, time, name, partySize, tableIds, status');
      if (error) throw error;
      return data[0];
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservation', data.id] });
    },
  });
};



export const useUpdateReservationWhole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reservationId, name, time, partySize }) => {
      const { data, error } = await supabase
        .from('reservations')
        .update({ name, time, partySize })
        .eq('id', reservationId)
        .select('id, time, name, partySize, status');
      if (error) {
        console.error('Supabase error:', error);
        throw new Error(error.message);
      }
      return data[0];
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservation', data.id] });
    },
    onError: (error) => {
      console.error('Mutation error:', error);
    },
  });
};

export const useDeleteReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reservationId }) => {
      const { error } = await supabase
        .from('reservations')
        .delete()
        .eq('id', reservationId);
      if (error) throw error;
      return { id: reservationId }; // Return the ID for invalidation
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservation', result.id] });
    },
  });
};


export const useReservationsFreeTable = () => {
  const {profile} = useAuth(); 

  return useQuery({
    queryKey: ['reservations', 'freeTable'], // Unique key for this query
    queryFn: async () => {
      const { data, error } = await supabaseFreeTable
        .from('reservations')
        .select('*')
        .eq('status', 'WAITING')
        .eq('restaurantid', profile.restaurantid);

      if (error) throw error;

      // Ensure name and status are never null
      return data.map(res => ({
        ...res,
        name: res.name || 'Unknown',
        status: res.status || 'PENDING', // Use uppercase for consistency
      }));
    },
    // Enable query only if user is authenticated with supabaseFreeTable
    enabled: !!supabaseFreeTable.auth.getSession().then(({ data }) => data.session),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};



  export const useReservationByUser = (id) => {
    return useQuery({
      queryKey: ['reservationsadmin', {id}], 
      queryFn: async()=>{
        const {data, error} = await supabaseFreeTable.from('reservations').select('*').eq("userid", id).eq("status", "CONFIRMED"); 
        if (error){
          throw new Error(error.message); 
        }
        return data; 
  
  
      }
    });
  
  }


   export const useUpdateReservationFreeTable = () => {
    const queryClient = useQueryClient();
  
    return useMutation({
      async mutationFn({id,status, time,location}) {
        const { data, error } = await supabaseFreeTable
          .from('reservations')
          .update({ status, time,location })
          .eq('id', id)
          .select();
  
        if (error) {
          throw error;
        }
        return data;
      },
      async onSuccess(_, { id }) {
        await queryClient.invalidateQueries(['reservations', 'freeTable']);
        await queryClient.invalidateQueries({ queryKey: ['reservations'] });

      },
      onError(error) {
        console.log(error);
      },
    });
  };
    export const useUpdateReservationTime = () => {
    const queryClient = useQueryClient();
  
    return useMutation({
      async mutationFn({id,time,location}) {
        const { error } = await supabaseFreeTable
          .from('reservations')
          .update({
            new_time:time, 
            status: time||location?"PENDING":"WAITING",
            new_location:location
            
          })
          .eq('id', id)
          .select();
  
        if (error) {
          console.log(error)
          throw error;
          
        }
      },
      async onSuccess(_, { id }) {
        await queryClient.invalidateQueries(['reservations', id]);
      },
      onError(error) {
        console.log(error);
      },
    });
  };


  export const useDeleteReservationFreeTable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ freeTableId }) => {
      const { error } = await supabase
        .from('reservations')
        .delete()
        .eq('freeTableId', freeTableId);
      if (error) throw error;
      return { freeTableId }; // Return the ID for invalidation
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservation', result.id] });
    },
  });
};




  export const useCancelReservationFreeTable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ freeTableId }) => {
      const { error } = await supabase
        .from('reservations')
        .update({status:"canceled",tableIds:[]})
        .eq('freeTableId', freeTableId);
      if (error) throw error;
      return { freeTableId }; // Return the ID for invalidation
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservation', result.id] });
    },
  });
};

export const useCancelReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reservationId }) => {
      const { error } = await supabase
        .from('reservations')
        .update({status:"canceled",tableIds:[]})
        .eq('id', reservationId);
      if (error) throw error;
      return { id: reservationId }; // Return the ID for invalidation
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservation', result.id] });
    },
  });
};




export const useNoShowReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reservationId }) => {
      const { error } = await supabase
        .from('reservations')
        .update({status:"noshow",tableIds:[]})
        .eq('id', reservationId);
      if (error) throw error;
      return { id: reservationId }; // Return the ID for invalidation
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservation', result.id] });
    },
  });
};


   export const useNoShowFreeTable = () => {
    const queryClient = useQueryClient();
  
    return useMutation({
      async mutationFn({id}) {
        const { data, error } = await supabaseFreeTable
          .from('reservations')
          .update({status:"NOSHOW"})
          .eq('id', id)
          .select();
  
        if (error) {
          throw error;
        }
        return data;
      },
      async onSuccess(_, { id }) {
        await queryClient.invalidateQueries(['reservations', 'freeTable']);
        await queryClient.invalidateQueries({ queryKey: ['reservations'] });

      },
      onError(error) {
        console.log(error);
      },
    });
  };


     export const useCancelReservationFreeTableApp = () => {
    const queryClient = useQueryClient();
  
    return useMutation({
      async mutationFn({id}) {
        const { data, error } = await supabaseFreeTable
          .from('reservations')
          .update({status:"CANCELED"})
          .eq('id', id)
          .select();
  
        if (error) {
          throw error;
        }
        return data;
      },
      async onSuccess(_, { id }) {
        await queryClient.invalidateQueries(['reservations', 'freeTable']);
        await queryClient.invalidateQueries({ queryKey: ['reservations'] });

      },
      onError(error) {
        console.log(error);
      },
    });
  };


     export const useUpdateReservationInfoFreeTable = () => {
    const queryClient = useQueryClient();
  
    return useMutation({
      async mutationFn({id,time,date,people}) {
        const { data, error } = await supabaseFreeTable
          .from('reservations')
          .update({time,date,people })
          .eq('id', id)
          .select();
  
        if (error) {
          throw error;
        }
        return data;
      },
      async onSuccess(_, { id }) {
        await queryClient.invalidateQueries(['reservations', 'freeTable']);
        await queryClient.invalidateQueries({ queryKey: ['reservations'] });

      },
      onError(error) {
        console.log(error);
      },
    });
  };


  export const useUnconfirmedReservationsFreeTable = () => {
        const {profile} = useAuth(); 

  return useQuery({
    queryKey: ['reservations', 'unconfirmedFreeTable'],
    queryFn: async () => {
      const { data, error } = await supabaseFreeTable
        .from('unconfirmedReservations')
        .select('*')
        .eq('status', 'PENDING').eq("restaurantId",profile.restaurantid); // Assuming unconfirmed reservations are marked as PENDING
      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
  });
};